import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import './Onboarding.css'

// ─────────────────────────────────────────────────────────────────────────────
// Onboarding — welcome presentation
// 5 slides explaining the platform philosophy, structure and rules.
// On completion: marks onboarding_complete, creates progress row, goes to /dashboard
// ─────────────────────────────────────────────────────────────────────────────

const TOTAL = 5

export function Onboarding() {
  const { user, refreshProfile } = useAuth()
  const navigate = useNavigate()
  const [current, setCurrent] = useState(0)
  const [dir, setDir]         = useState('forward') // 'forward' | 'back'
  const [saving, setSaving]   = useState(false)
  const [finishError, setFinishError] = useState('')

  // Keyboard navigation — empty deps intentional, setters are stable
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'ArrowRight' || e.key === 'Enter') handleNext()
      if (e.key === 'ArrowLeft') handlePrev()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Touch swipe — empty deps intentional, setters are stable
  useEffect(() => {
    let startX = 0
    function onStart(e) { startX = e.touches[0].clientX }
    function onEnd(e) {
      const diff = startX - e.changedTouches[0].clientX
      if (Math.abs(diff) > 50) {
        if (diff > 0) handleNext()
        else handlePrev()
      }
    }
    window.addEventListener('touchstart', onStart)
    window.addEventListener('touchend', onEnd)
    return () => {
      window.removeEventListener('touchstart', onStart)
      window.removeEventListener('touchend', onEnd)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleNext() {
    if (current < TOTAL - 1) {
      setDir('forward')
      setCurrent(c => c + 1)
    }
  }

  function handlePrev() {
    if (current > 0) {
      setDir('back')
      setCurrent(c => c - 1)
    }
  }

  async function handleFinish() {
    if (saving) return
    setSaving(true)
    setFinishError('')
    try {
      const { error: profileErr } = await supabase.from('profiles').upsert({
        id:                  user.id,
        placement_tier:      1,
        onboarding_complete: true,
        updated_at:          new Date().toISOString(),
      }, { onConflict: 'id' })

      if (profileErr) {
        // Surface failure rather than navigating to a half-state. Without
        // this, the user lands on /dashboard with onboarding_complete=false
        // and ProtectedRoute bounces them right back here — an infinite loop.
        console.error('Onboarding: profile upsert failed:', profileErr)
        setFinishError("Couldn't save your progress. Check your connection and try again.")
        setSaving(false)
        return
      }

      const { error: progressErr } = await supabase.from('progress').upsert({
        user_id:             user.id,
        completed_lessons:   [],
        quiz_scores:         {},
        capstone_passed:     false,
        certificate_unlocked: false,
      }, { onConflict: 'user_id', ignoreDuplicates: true })

      if (progressErr) {
        // Progress row probably exists already — ignoreDuplicates handles
        // that. If it's a different error, surface it but don't block:
        // the profile flag is set, so onboarding can be considered done.
        console.warn('Onboarding: progress upsert returned an error (continuing):', progressErr)
      }

      await refreshProfile()
      navigate('/dashboard', { replace: true })
    } catch (e) {
      console.error('Onboarding: handleFinish threw:', e)
      setFinishError("Couldn't save your progress. Check your connection and try again.")
      setSaving(false)
    }
  }

  const isLast = current === TOTAL - 1

  return (
    <div className="ob-shell">
      <div className="ob-grid-bg" aria-hidden="true" />

      {/* Top bar */}
      <div className="ob-topbar">
        <div className="ob-logo">
          <div className="ob-logo__mark">
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
              <rect x="1" y="4" width="14" height="9" rx="1.5" stroke="#0a0c10" strokeWidth="1.5"/>
              <path d="M5 8h6M5 10.5h3" stroke="#0a0c10" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M6 2h4" stroke="#0a0c10" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <span className="ob-logo__name mono">TechBench</span>
        </div>
        <button className="ob-skip mono" onClick={handleFinish} disabled={saving}>
          Skip →
        </button>
      </div>

      {/* Progress dots */}
      <div className="ob-dots">
        {Array.from({ length: TOTAL }).map((_, i) => (
          <div
            key={i}
            className={`ob-dot ${i < current ? 'ob-dot--done' : ''} ${i === current ? 'ob-dot--active' : ''}`}
          />
        ))}
      </div>

      {/* Slides */}
      <div className="ob-slides">
        <Slide1 active={current === 0} dir={dir} />
        <Slide2 active={current === 1} dir={dir} />
        <Slide3 active={current === 2} dir={dir} />
        <Slide4 active={current === 3} dir={dir} />
        <Slide5 active={current === 4} dir={dir} />
      </div>

      {/* Navigation */}
      <div className="ob-nav">
        {current > 0 && (
          <button className="ob-btn-back" onClick={handlePrev}>← Back</button>
        )}
        {isLast ? (
          <button className="ob-btn-finish" onClick={handleFinish} disabled={saving}>
            {saving ? 'Loading...' : 'Go to my map →'}
          </button>
        ) : (
          <button className="ob-btn-next" onClick={handleNext}>
            Next
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        )}
      </div>

      {finishError && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(255, 107, 107, 0.12)',
          border: '1px solid rgba(255, 107, 107, 0.32)',
          color: '#ff6b6b',
          fontFamily: 'var(--font-mono)', fontSize: '0.78rem',
          borderRadius: '10px', padding: '12px 20px',
          zIndex: 200, maxWidth: 'calc(100vw - 32px)', textAlign: 'center',
        }}>
          {finishError}
        </div>
      )}
    </div>
  )
}

// ── Slide wrapper ─────────────────────────────────────────────────────────────
function Slide({ active, dir, children }) {
  return (
    <div className={`ob-slide ${active ? 'ob-slide--active' : ''} ${!active && dir === 'forward' ? 'ob-slide--prev' : ''}`}>
      <div className="ob-slide__inner">{children}</div>
    </div>
  )
}

// ── Slide 1 — Welcome ─────────────────────────────────────────────────────────
function Slide1({ active, dir }) {
  return (
    <Slide active={active} dir={dir}>
      <div className="ob-eyebrow ob-eyebrow--accent mono">Welcome to TechBench</div>
      <h1 className="ob-title">Built for people who want to go <em>somewhere</em>.</h1>
      <p className="ob-body">
        TechBench is not a course catalogue. It's a structured career progression platform —
        Foundation to senior, generalist to specialist, in a sequence that{' '}
        <strong>reflects how IT careers actually work</strong>.
        <br/><br/>
        Three minutes. Then you'll see exactly where you're going.
      </p>
    </Slide>
  )
}

// ── Slide 2 — Three tracks ────────────────────────────────────────────────────
function Slide2({ active, dir }) {
  return (
    <Slide active={active} dir={dir}>
      <div className="ob-eyebrow ob-eyebrow--teal mono">Foundation</div>
      <h1 className="ob-title">Three tracks. One <span className="ob-teal">spine</span>.</h1>
      <div className="ob-tracks">
        <div className="ob-track ob-track--it">
          <div className="ob-track__label mono">IT Foundation</div>
          <div className="ob-track__name">The technical base</div>
          <div className="ob-track__desc">Hardware, networking, OS, mobile, and troubleshooting methodology. 46 lessons across 5 modules.</div>
        </div>
        <div className="ob-track ob-track--hs">
          <div className="ob-track__label mono">Human Skills</div>
          <div className="ob-track__name">The professional base</div>
          <div className="ob-track__desc">Communication, pressure, difficult situations, and interview prep. The skills no cert teaches. 25 lessons.</div>
        </div>
        <div className="ob-track ob-track--ai">
          <div className="ob-track__label mono">AI Foundations</div>
          <div className="ob-track__name">The modern base</div>
          <div className="ob-track__desc">Working with AI deliberately, building automations, and staying accountable. 25 lessons.</div>
        </div>
      </div>
      <p className="ob-body" style={{ marginBottom: 0 }}>
        All three run simultaneously from day one —
        because the job requires all three at the same time.
      </p>
    </Slide>
  )
}

// ── Slide 3 — Four rules ──────────────────────────────────────────────────────
function Slide3({ active, dir }) {
  return (
    <Slide active={active} dir={dir}>
      <div className="ob-eyebrow ob-eyebrow--muted mono">How it works</div>
      <h1 className="ob-title">Four rules. No <em>exceptions</em>.</h1>
      <div className="ob-rules">
        {[
          { n:'01', title:'One direction. No downgrading.', desc:'Once you reach a tier, you stay at that price permanently. Progress is earned, not rented.' },
          { n:'02', title:'Capstone gate.', desc:'You pass the capstone before unlocking the next tier. The upgrade option doesn\'t appear until you\'re eligible.' },
          { n:'03', title:'Branch unlock.', desc:'From T2, add a second career tree for a one-time fee. No subscription change. Progress independently.' },
          { n:'04', title:'Pause and restore.', desc:'Pause any time. Progress, unlocks, certificates — all saved. Resubscribe and pick up exactly where you left off.' },
        ].map(r => (
          <div key={r.n} className="ob-rule">
            <div className="ob-rule__num">{r.n}</div>
            <div className="ob-rule__title">{r.title}</div>
            <div className="ob-rule__desc">{r.desc}</div>
          </div>
        ))}
      </div>
    </Slide>
  )
}

// ── Slide 4 — Career map ──────────────────────────────────────────────────────
function Slide4({ active, dir }) {
  return (
    <Slide active={active} dir={dir}>
      <div className="ob-eyebrow ob-eyebrow--accent mono">Your dashboard</div>
      <h1 className="ob-title">The career <em>map</em>.</h1>
      <p className="ob-body">
        Every session starts here. Your Foundation tracks above, career trees below.
        The map shows where you are, what's next, and what's waiting further down.
      </p>
      <div className="ob-map-preview">
        <div className="ob-map-label mono">Tier 1 — Foundation</div>
        <div className="ob-map-shared">
          <div className="ob-map-cell ob-map-cell--hs">Human Skills</div>
          <div className="ob-map-cell ob-map-cell--ai">AI Foundations</div>
        </div>
        <div className="ob-map-wide">
          <div className="ob-map-cell ob-map-cell--it">IT Foundation — 46 lessons</div>
        </div>
        <div className="ob-map-wide">
          <div className="ob-map-cell ob-map-cell--cap">◆ Foundation Capstone — Getting Real</div>
        </div>
        <div className="ob-map-label mono" style={{ marginTop: 10 }}>Tier 2+ — Specialist trees</div>
        <div className="ob-map-trees">
          {['Infrastructure','ITSM','Cloud','Security','Data'].map(t => (
            <div key={t} className="ob-map-cell ob-map-cell--locked">{t}</div>
          ))}
        </div>
      </div>
    </Slide>
  )
}

// ── Slide 5 — Ready ───────────────────────────────────────────────────────────
function Slide5({ active, dir }) {
  return (
    <Slide active={active} dir={dir}>
      <div className="ob-final-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="5 3 19 12 5 21 5 3"/>
        </svg>
      </div>
      <div className="ob-eyebrow ob-eyebrow--accent mono">You're ready</div>
      <h1 className="ob-title">Foundation is where it <em>starts</em>.</h1>
      <p className="ob-body">
        Everyone begins at Foundation — regardless of experience.
        It's not the easy part before the real work.{' '}
        <strong>It is the real work.</strong>
        <br/><br/>
        Your map is waiting. Let's go.
      </p>
    </Slide>
  )
}
